import { Box } from '@apia/theme';
import { Icon, type TIconName } from '@apia/icons';
import { useAccordionContext } from '../context';
import { ISimpleButton, SimpleButton } from '../../forms';
import { Checkbox } from './Checkbox';
import { useIsChecked } from './useIsChecked';
import { KeyboardEvent, MouseEvent, ReactNode, useCallback } from 'react';
import { AutoEllipsis } from '../../responsive';

export interface IAccordionItemButton {
  ariaLabel: string;
  /**
   * Si checked !== undefined => se mostrará un checkbox.
   */
  checked?: boolean;
  /**
   * Solamente se utiliza en conjunto con el checkbox y permite evitar que el
   * usuario pueda marcar o desmarcar el checkbox.
   */
  className?: string;
  disableSelection?: boolean;
  domButtonProps?: ISimpleButton;
  label: string;
  /**
   * Evento que se dispara cuando el usuario hace click en el checkbox o
   * presiona espacio sobre el botón del acordeón.
   */
  onChange?: (checked: boolean) => unknown;
  onClick?: (ev: MouseEvent) => unknown;
  onUserPressEnter?: (ev: KeyboardEvent) => unknown;
  rightButtons?: ReactNode;
  rightIcons?: TIconName[] | TIconName;
  tabIndex?: number;
  /**
   * Si no se pasa title se usará ariaLabel.
   */
  title?: string;
}

export const DefaultAccordionItemButton = (props: IAccordionItemButton) => {
  const handler = useAccordionContext();

  const { isExpanded } = handler.hooks.useItemStateSelector((props) => props);
  const id = handler.hooks.useItemId();
  const [isChecked, setIsChecked] = useIsChecked(props);
  const handleClick = useCallback(
    (ev: MouseEvent) => {
      props.onClick?.(ev);
      if (!ev.isDefaultPrevented()) handler.toggleItem(id, !isExpanded);
    },
    [handler, id, isExpanded, props],
  );
  const handleKeyDown = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.code === 'Enter') {
        props.onUserPressEnter?.(ev);
        if (!ev.isDefaultPrevented() && props.rightButtons)
          handler.toggleItem(id, !isExpanded);
      }
    },
    [handler, id, isExpanded, props],
  );

  return (
    <SimpleButton
      {...props.domButtonProps}
      className={`accordion__item__button ${props.className ?? ''} ${
        props.domButtonProps?.className ?? ''
      }`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-expanded={isExpanded}
      aria-controls={`accordion__${handler.id}__${id}`}
      title={props.title}
      aria-label={props.ariaLabel}
      tabIndex={props.tabIndex}
    >
      <Box className="accordion__item__button__leftGroup">
        {props.checked !== undefined && (
          <Checkbox
            checked={isChecked}
            disabled={props.disableSelection}
            onChange={setIsChecked}
          />
        )}
        <Box as="h3" className="accordion__item__button__label">
          <AutoEllipsis overrideStyles={() => ({ textAlign: 'left' })}>
            {props.label ?? props.title ?? props.ariaLabel}
          </AutoEllipsis>
        </Box>
      </Box>
      <Box className="accordion__item__button__rightGroup">
        {props.rightButtons}
        <Icon
          name={isExpanded ? 'ArrowUpThin' : 'ArrowDownThin'}
          title={props.title ?? props.ariaLabel}
          size={23}
        />
      </Box>
    </SimpleButton>
  );
};
